import React, { useEffect, useMemo, useState } from 'react';
import secureStorage from '../utils/secureStorage';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAgencyContext, useToast } from '../contexts/AppContexts';
import { processImageFile, IMAGE_CONFIG } from '../utils/imageCompression';
import { ArrowLeft, Camera, CheckCircle, Plus, Save, Trash, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';

const API = `${API_BASE_URL}/api`;

const validateLatitude = (lat) => {
  if (lat === '' || lat === null || lat === undefined) return true;
  const formatRegex = /^-?\d+\.?\d{0,4}$/;
  if (!formatRegex.test(lat.toString())) return false;
  const num = parseFloat(lat);
  return !isNaN(num) && num >= -90.0000 && num <= 90.0000;
};

const validateLongitude = (lng) => {
  if (lng === '' || lng === null || lng === undefined) return true;
  const formatRegex = /^-?\d+\.?\d{0,4}$/;
  if (!formatRegex.test(lng.toString())) return false;
  const num = parseFloat(lng);
  return !isNaN(num) && num >= -180.0000 && num <= 180.0000;
};

const formatPhone = (input) => {
  const digits = input.replace(/\D/g, '');
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  } else {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
};

const validatePhone = (phone) => {
  const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
  return phoneRegex.test(phone);
};

const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const ParticipantForm = ({ onBack, onSave, editingParticipant = null, positions = [], exerciseId = null }) => {
  const { selectedAgency } = useAgencyContext();

  // Auto-scroll to top when the form mounts (fresh for Add or Edit) so the user
  // always starts at the first field instead of wherever the list was scrolled.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, []);
  
  // Canadian Provinces
  const canadianProvinces = [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 
    'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 
    'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 
    'Saskatchewan', 'Yukon'
  ];
  
  // US States
  const usStates = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
    'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
    'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
    'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
    'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
    'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
    'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia'
  ];
  
  // UK Countries and Regions
  const ukRegions = [
    // Countries
    'England', 'Scotland', 'Wales', 'Northern Ireland',
    // English Regions
    'East Midlands', 'East of England', 'Greater London', 'North East England',
    'North West England', 'South East England', 'South West England',
    'West Midlands', 'Yorkshire and the Humber',
    // Scottish Council Areas
    'Aberdeenshire', 'Angus', 'Argyll and Bute', 'City of Edinburgh',
    'Dumfries and Galloway', 'Dundee City', 'East Ayrshire', 'Fife',
    'Glasgow City', 'Highland', 'Perth and Kinross', 'Scottish Borders',
    // Welsh Principal Areas
    'Cardiff', 'Swansea', 'Newport', 'Wrexham', 'Flintshire', 
    'Gwynedd', 'Pembrokeshire', 'Carmarthenshire',
    // Northern Ireland Counties
    'Antrim', 'Armagh', 'Down', 'Fermanagh', 'Londonderry', 'Tyrone'
  ];
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    position: '',
    languages: '',
    skillsQuals: '',
    assignedTo: '',
    address: '',
    city: '',
    provinceState: '',
    country: 'Canada',
    homePhone: '',
    cellPhone: '',
    email: '',
    latitude: '',
    longitude: '',
    involvedInExercise: false,
    profileImage: null,
    // Last Day Worked section fields
    ldw: '',
    orderRequestNumber: '',
    checkInDate: '',
    checkInTime: '',
    leadersName: '',
    homeBase: '',
    departurePoint: '',
    methodOfTravel: '',
    currentAssignedLocation: '',
    assignedExercise: '',
    requestedArrDate: '',
    requestedArrTime: '',
    taskForce: '',
    strikeTeam: '',
    qualifications: '',
    sentToResourceUnit: false,
    organization: '',
    // Back of T-Card fields
    tcardIncidentLocation: '',
    tcardTime: '',
    tcardStatusAssigned: false,
    tcardStatusOSRest: false,
    tcardStatusOSPers: false,
    tcardStatusAvailable: false,
    tcardStatusOSMech: false,
    tcardStatusETR: false,
    tcardNotes: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [showAddLocationDialog, setShowAddLocationDialog] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false); // For custom location dropdown with delete
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationDescription, setNewLocationDescription] = useState('');
  const [strikeTeams, setStrikeTeams] = useState([]);
  const [taskForces, setTaskForces] = useState([]);
  
  // Toast for notifications
  const toast = useToast();

  // Scroll to top when the participant editor mounts (open create/edit view)
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    } catch (_) { /* noop */ }
  }, []);
  
  // Fetch all exercises
  useEffect(() => {
    const fetchExercises = async () => {
      try {
        const token = secureStorage.getToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        // Add organization_id filter if Super Admin has selected an agency
        let url = `${API_BASE_URL}/api/exercise-builder`;
        if (selectedAgency) {
          url += `?organization_id=${selectedAgency.id}`;
        }
        
        const response = await fetch(url, { headers });
        if (response.ok) {
          const data = await response.json();
          setExercises(data || []);
        }
      } catch (error) {
        console.error('Error fetching exercises:', error);
      }
    };
    fetchExercises();
  }, [selectedAgency]);
  
  // Fetch strike teams for the current exercise or participant's assigned exercise
  const effectiveExerciseId = exerciseId || formData.assignedExercise;
  useEffect(() => {
    const fetchStrikeTeams = async () => {
      if (!effectiveExerciseId) { setStrikeTeams([]); return; }
      try {
        const token = secureStorage.getToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const response = await fetch(`${API_BASE_URL}/api/strike-teams?exercise_id=${effectiveExerciseId}`, { headers });
        if (response.ok) {
          const data = await response.json();
          setStrikeTeams(data || []);
        }
      } catch (error) {
        console.error('Error fetching strike teams:', error);
      }
    };
    fetchStrikeTeams();
  }, [effectiveExerciseId]);

  // Fetch task forces for the current exercise or participant's assigned exercise
  useEffect(() => {
    const fetchTaskForces = async () => {
      if (!effectiveExerciseId) { setTaskForces([]); return; }
      try {
        const token = secureStorage.getToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const response = await fetch(`${API_BASE_URL}/api/task-forces?exercise_id=${effectiveExerciseId}`, { headers });
        if (response.ok) {
          const data = await response.json();
          setTaskForces(data || []);
        }
      } catch (error) {
        console.error('Error fetching task forces:', error);
      }
    };
    fetchTaskForces();
  }, [effectiveExerciseId]);

  // Validation error states
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    homePhone: '',
    cellPhone: '',
    latitude: '',
    longitude: ''
  });

  // Positions array moved to AppContent component - access via props

  // Default fallback locations if none are managed
  const defaultLocations = [
    'Command Post',
    'Hospital',
    'Fire Station',
    'Police Station',
    'Operations Center EOC',
    'Staging Area'
  ];

  // Dynamic assigned to options using managed locations + fallbacks - using useMemo to prevent infinite loops
  const assignedToOptions = React.useMemo(() => {
    // Use map marker names as location options
    return locations.map(location => location.name);
  }, [locations]);
  
  // Create merged positions array to ensure current position is included - using useMemo
  const availablePositions = React.useMemo(() => {
    let positionsList = [...positions];
    if (editingParticipant && editingParticipant.position && !positions.includes(editingParticipant.position)) {
      positionsList = [editingParticipant.position, ...positions];
    }
    return positionsList;
  }, [positions, editingParticipant?.position]);

  const countries = [
    'Canada',
    'USA',
    'United Kingdom',
    '---',
    'Afghanistan',
    'Albania',
    'Algeria',
    'Argentina',
    'Australia',
    'Austria',
    'Bangladesh',
    'Belgium',
    'Brazil',
    'Bulgaria',
    'Chile',
    'China',
    'Colombia',
    'Czech Republic',
    'Denmark',
    'Egypt',
    'Finland',
    'France',
    'Germany',
    'Greece',
    'India',
    'Indonesia',
    'Iran',
    'Iraq',
    'Ireland',
    'Israel',
    'Italy',
    'Japan',
    'Jordan',
    'Kenya',
    'South Korea',
    'Mexico',
    'Netherlands',
    'New Zealand',
    'Norway',
    'Pakistan',
    'Philippines',
    'Poland',
    'Portugal',
    'Romania',
    'Russia',
    'Saudi Arabia',
    'South Africa',
    'Spain',
    'Sweden',
    'Switzerland',
    'Thailand',
    'Turkey',
    'Ukraine',
    'Vietnam'
  ];

  // Validation helper functions
  const validateEmailField = (email) => {
    if (!email.trim()) return ''; // Field is not required
    if (!validateEmail(email)) {
      return 'Please enter a valid email address (e.g., username@domain.com)';
    }
    return '';
  };

  const validatePhoneField = (phone) => {
    if (!phone.trim()) return ''; // Field is not required
    if (!validatePhone(phone)) {
      return 'Please enter phone number in format 123-456-7890';
    }
    return '';
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setFormData(prev => ({ ...prev, email }));
    
    // Real-time validation
    const error = validateEmailField(email);
    setValidationErrors(prev => ({ ...prev, email: error }));
  };

  const handleHomePhoneChange = (e) => {
    const phone = formatPhone(e.target.value);
    setFormData(prev => ({ ...prev, homePhone: phone }));
    
    // Real-time validation
    const error = validatePhoneField(phone);
    setValidationErrors(prev => ({ ...prev, homePhone: error }));
  };

  const handleCellPhoneChange = (e) => {
    const phone = formatPhone(e.target.value);
    setFormData(prev => ({ ...prev, cellPhone: phone }));
    
    // Real-time validation
    const error = validatePhoneField(phone);
    setValidationErrors(prev => ({ ...prev, cellPhone: error }));
  };

  // Validation helper functions for participant coordinates
  const validateParticipantLatitudeField = (lat) => {
    if (lat === '' || lat === null || lat === undefined) return ''; // Field is not required
    if (!validateLatitude(lat)) {
      return 'Please enter latitude in format 45.1234 (range: -90.0000 to 90.0000)';
    }
    return '';
  };

  const validateParticipantLongitudeField = (lng) => {
    if (lng === '' || lng === null || lng === undefined) return ''; // Field is not required
    if (!validateLongitude(lng)) {
      return 'Please enter longitude in format 97.0000 (range: -180.0000 to 180.0000)';
    }
    return '';
  };

  const handleParticipantLatitudeChange = (e) => {
    const lat = e.target.value;
    setFormData(prev => ({ ...prev, latitude: lat }));
    
    // Real-time validation
    const error = validateParticipantLatitudeField(lat);
    setValidationErrors(prev => ({ ...prev, latitude: error }));
  };

  const handleParticipantLongitudeChange = (e) => {
    const lng = e.target.value;
    setFormData(prev => ({ ...prev, longitude: lng }));
    
    // Real-time validation
    const error = validateParticipantLongitudeField(lng);
    setValidationErrors(prev => ({ ...prev, longitude: error }));
  };

  useEffect(() => {
    if (editingParticipant) {
      setFormData({
        firstName: editingParticipant.firstName || '',
        lastName: editingParticipant.lastName || '',
        position: editingParticipant.position || '',
        languages: editingParticipant.languages || '',
        skillsQuals: editingParticipant.skillsQuals || '',
        assignedTo: editingParticipant.assignedTo || '',
        address: editingParticipant.address || '',
        city: editingParticipant.city || '',
        provinceState: editingParticipant.provinceState || '',
        country: editingParticipant.country || 'Canada',
        homePhone: editingParticipant.homePhone || '',
        cellPhone: editingParticipant.cellPhone || '',
        email: editingParticipant.email || '',
        latitude: editingParticipant.latitude || '',
        longitude: editingParticipant.longitude || '',
        involvedInExercise: editingParticipant.involvedInExercise || false,
        profileImage: editingParticipant.profileImage || null,
        // Last Day Worked section fields
        ldw: editingParticipant.ldw || '',
        orderRequestNumber: editingParticipant.orderRequestNumber || '',
        checkInDateTime: editingParticipant.checkInDateTime || '',
        checkInDate: editingParticipant.checkInDate || (editingParticipant.checkInDateTime ? editingParticipant.checkInDateTime.split('T')[0] : ''),
        checkInTime: editingParticipant.checkInTime || (editingParticipant.checkInDateTime ? editingParticipant.checkInDateTime.split('T')[1] : ''),
        leadersName: editingParticipant.leadersName || '',
        homeBase: editingParticipant.homeBase || '',
        departurePoint: editingParticipant.departurePoint || '',
        methodOfTravel: editingParticipant.methodOfTravel || '',
        currentAssignedLocation: editingParticipant.currentAssignedLocation || '',
        assignedExercise: editingParticipant.assignedExercise || editingParticipant.assigned_exercise || '',
        requestedArrDate: editingParticipant.requestedArrDate || '',
        requestedArrTime: editingParticipant.requestedArrTime || '',
        taskForce: (editingParticipant.taskForce && editingParticipant.taskForce !== 'none') ? editingParticipant.taskForce : '',
        strikeTeam: (editingParticipant.strikeTeam && editingParticipant.strikeTeam !== 'none') ? editingParticipant.strikeTeam : '',
        qualifications: editingParticipant.qualifications || '',
        sentToResourceUnit: editingParticipant.sentToResourceUnit || false,
        organization: editingParticipant.organization || '',
        // Back of T-Card fields
        tcardIncidentLocation: editingParticipant.tcardIncidentLocation || '',
        tcardTime: editingParticipant.tcardTime || '',
        tcardStatusAssigned: editingParticipant.tcardStatusAssigned || false,
        tcardStatusOSRest: editingParticipant.tcardStatusOSRest || false,
        tcardStatusOSPers: editingParticipant.tcardStatusOSPers || false,
        tcardStatusAvailable: editingParticipant.tcardStatusAvailable || false,
        tcardStatusOSMech: editingParticipant.tcardStatusOSMech || false,
        tcardStatusETR: editingParticipant.tcardStatusETR || false,
        tcardNotes: editingParticipant.tcardNotes || ''
      });
      if (editingParticipant.profileImage) {
        setImagePreview(editingParticipant.profileImage);
      }
    }
  }, [editingParticipant, availablePositions, assignedToOptions]);

  // Fetch map markers as locations (from Mapping menu) - filtered by current exercise
  useEffect(() => {
    const fetchMapMarkers = async () => {
      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        // Use exerciseId prop (current exercise) first, fall back to assigned exercise
        const targetExerciseId = exerciseId || formData.assignedExercise;
        let url;
        if (targetExerciseId) {
          const matchedExercise = exercises.find(e => e.id === targetExerciseId || e.exercise_name === targetExerciseId);
          const exId = matchedExercise ? matchedExercise.id : targetExerciseId;
          url = `${API}/map-objects?type=marker&exercise_id=${exId}`;
        } else {
          url = `${API}/map-objects?type=marker`;
        }
        const response = await axios.get(url, { headers });
        const markers = response.data.map(marker => ({
          id: marker.id,
          name: marker.name
        }));
        setLocations(markers);
      } catch (error) {
        console.error('Error fetching map markers:', error);
        setLocations([]);
      }
    };
    
    fetchMapMarkers();
  }, [exerciseId, formData.assignedExercise, exercises]);

  // Add new location handler
  const handleAddLocation = async () => {
    if (!newLocationName.trim()) {
      toast.error('Please enter a location name');
      return;
    }

    // Check if location already exists
    if (locations.some(l => l.name.toLowerCase() === newLocationName.toLowerCase()) || 
        defaultLocations.some(d => d.toLowerCase() === newLocationName.toLowerCase())) {
      toast.error('Location already exists');
      return;
    }

    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const locationData = {
        name: newLocationName,
        description: newLocationDescription.trim() || 'Check-in location'
      };
      
      console.log('🔵 Adding location:', locationData);
      console.log('🔵 API endpoint:', `${API}/locations`);
      console.log('🔵 Headers:', headers);
      
      const response = await axios.post(`${API}/locations`, locationData, { headers });
      
      console.log('✅ Location added successfully:', response.data);
      
      setLocations(prev => [...prev, response.data]);
      setNewLocationName('');
      setNewLocationDescription('');
      toast.success(`Location "${newLocationName}" added successfully`);
    } catch (error) {
      console.error('❌ Error adding location:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Status code:', error.response?.status);
      
      // Handle different error formats
      let errorMessage = 'Failed to add location. Please try again.';
      
      if (error.response?.data) {
        const data = error.response.data;
        
        // Handle Pydantic validation errors (array of error objects)
        if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        }
        // Handle string detail message
        else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
        // Handle message field
        else if (typeof data.message === 'string') {
          errorMessage = data.message;
        }
        // Handle object detail by converting to string
        else if (typeof data.detail === 'object') {
          errorMessage = JSON.stringify(data.detail);
        }
      }
      
      toast.error(errorMessage);
    }
  };

  // Delete location handler
  const handleDeleteLocation = async (locationId, locationName) => {
    if (formData.assignedTo === locationName) {
      toast.error('Cannot delete currently selected location');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${locationName}"?`)) {
      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.delete(`${API}/locations/${locationId}`, { headers });
        setLocations(prev => prev.filter(loc => loc.id !== locationId));
        toast.success(`Location "${locationName}" deleted successfully`);
      } catch (error) {
        console.error('Error deleting location:', error);
        console.error('Error details:', error.response?.data);
        
        // Handle different error formats
        let errorMessage = 'Failed to delete location';
        
        if (error.response?.data) {
          const data = error.response.data;
          
          if (Array.isArray(data.detail)) {
            errorMessage = data.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (typeof data.message === 'string') {
            errorMessage = data.message;
          } else if (typeof data.detail === 'object') {
            errorMessage = JSON.stringify(data.detail);
          }
        }
        
        toast.error(errorMessage);
      }
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Use image compression utility for profile images (400x400, max 150KB)
      const result = await processImageFile(file, 'profile');
      if (result.success) {
        setImagePreview(result.data);
        setFormData(prev => ({ ...prev, profileImage: result.data }));
      } else {
        alert(result.error || 'Failed to process image');
      }
    }
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const result = await processImageFile(file, 'profile');
      if (result.success) {
        setImagePreview(result.data);
        setFormData(prev => ({ ...prev, profileImage: result.data }));
      } else {
        alert(result.error || 'Failed to process image');
      }
    }
  };

  const handleCameraCapture = async () => {
    // Fast path: try getUserMedia (Chrome/Firefox desktop, dev preview).
    // On production the site is served behind a proxy that ships a
    // `Permissions-Policy: camera=()` header which disables getUserMedia,
    // so we fall back to the mobile-native file input with capture="environment"
    // which invokes the OS camera app directly and needs no permission policy.
    const gum = navigator?.mediaDevices?.getUserMedia;
    if (typeof gum !== 'function') {
      // API is unavailable (insecure context OR permissions-policy strip) → fallback
      document.getElementById('participant-image-camera')?.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();

      video.addEventListener('loadedmetadata', () => {
        const canvas = document.createElement('canvas');
        // Limit camera capture to profile image size (400x400)
        const maxSize = IMAGE_CONFIG.profile.maxWidth;
        const scale = Math.min(maxSize / video.videoWidth, maxSize / video.videoHeight, 1);
        canvas.width = video.videoWidth * scale;
        canvas.height = video.videoHeight * scale;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = canvas.toDataURL('image/jpeg', IMAGE_CONFIG.profile.quality);
        setImagePreview(imageData);
        setFormData(prev => ({ ...prev, profileImage: imageData }));

        stream.getTracks().forEach(track => track.stop());
      });
    } catch (error) {
      // NotAllowedError, NotFoundError, NotSupportedError, SecurityError, etc.
      // Every failure mode → fall back to native file-input + capture pattern.
      console.warn('getUserMedia unavailable, falling back to file-input capture:', error?.name || error);
      document.getElementById('participant-image-camera')?.click();
    }
  };

  const handleCameraFileFallback = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    const result = await processImageFile(file, 'profile');
    if (result.success) {
      setImagePreview(result.data);
      setFormData(prev => ({ ...prev, profileImage: result.data }));
    } else {
      alert(result.error || 'Failed to process image');
    }
    // Allow re-selecting the same file next time
    e.target.value = '';
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.cellPhone || formData.cellPhone.trim() === '') {
      setValidationErrors(prev => ({ ...prev, cellPhone: 'Cell Phone is required' }));
      alert('Please enter a Cell Phone number. This field is required.');
      return;
    }
    
    setLoading(true);
    
    try {
      const participantData = {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.cellPhone || formData.homePhone,
        address: formData.address, // Include the individual address field
        fullAddress: `${formData.address}, ${formData.city}, ${formData.provinceState}`, // Keep full address for compatibility
        organization: formData.organization || '', // Agency/Organization field
        role: formData.position.toLowerCase().replace(/\s+/g, '_'),
        firstName: formData.firstName,
        lastName: formData.lastName,
        position: formData.position,
        languages: formData.languages,
        skillsQuals: formData.skillsQuals,
        assignedTo: formData.assignedTo,
        city: formData.city,
        provinceState: formData.provinceState,
        country: formData.country,
        homePhone: formData.homePhone,
        cellPhone: formData.cellPhone,
        latitude: formData.latitude,
        longitude: formData.longitude,
        involvedInExercise: formData.involvedInExercise,
        profileImage: formData.profileImage,
        // Check-in Information fields
        ldw: formData.ldw,
        orderRequestNumber: formData.orderRequestNumber,
        checkInDateTime: formData.checkInDateTime,
        leadersName: formData.leadersName,
        homeBase: formData.homeBase,
        departurePoint: formData.departurePoint,
        methodOfTravel: formData.methodOfTravel,
        incidentAssignment: formData.incidentAssignment,
        assigned_exercise: formData.assignedExercise, // Assigned Exercise field
        assignedTo: formData.assignedTo, // Check-in Location field
        checkInLocation: formData.assignedTo, // Alias for Check-in Location
        taskForce: (formData.taskForce === 'none' || !formData.taskForce) ? '' : formData.taskForce,
        strikeTeam: (formData.strikeTeam === 'none' || !formData.strikeTeam) ? '' : formData.strikeTeam,
        qualifications: formData.qualifications,
        sentToResourceUnit: formData.sentToResourceUnit,
        // Back of T-Card fields
        tcardIncidentLocation: formData.tcardIncidentLocation,
        tcardTime: formData.tcardTime,
        tcardStatusAssigned: formData.tcardStatusAssigned,
        tcardStatusOSRest: formData.tcardStatusOSRest,
        tcardStatusOSPers: formData.tcardStatusOSPers,
        tcardStatusAvailable: formData.tcardStatusAvailable,
        tcardStatusOSMech: formData.tcardStatusOSMech,
        tcardStatusETR: formData.tcardStatusETR,
        tcardNotes: formData.tcardNotes
      };

      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (editingParticipant) {
        const response = await axios.put(`${API}/participants/${editingParticipant.id}`, participantData, { headers });
        onSave(response.data);
      } else {
        const response = await axios.post(`${API}/participants`, participantData, { headers });
        onSave(response.data);
      }
    } catch (error) {
      console.error('Error saving participant:', error);
      console.error('Error details:', error.response?.data);
      alert('Error saving participant. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const saveStepDraft = async () => {
    setLoading(true);
    try {
      // Save current form data as draft
      const draftData = {
        ...formData,
        isDraft: true
      };
      
      // You could save this to localStorage or send to backend
      secureStorage.setPreference('participantDraft', JSON.stringify(draftData));
      console.log('Participant step saved as draft:', draftData);
      
      // Show success message
      alert('Step saved successfully!');
    } catch (error) {
      console.error('Error saving step:', error);
      alert('Error saving step. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="text-gray-400 hover:text-orange-500 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Participants
        </Button>
        <h1 className="text-3xl font-bold text-orange-500 mb-2">
          {editingParticipant ? 'Edit Participant' : 'Add New Participant'}
        </h1>
        <p className="text-gray-400">Enter participant information for emergency exercise training</p>
      </div>

      <form onSubmit={handleSubmitLocal} className="space-y-6">
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Profile Image</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-6">
              <div 
                className={`w-32 h-32 rounded-lg bg-gray-700 border-2 border-dashed flex items-center justify-center overflow-hidden cursor-pointer transition-colors ${isDragging ? 'border-orange-500 bg-orange-500/10' : 'border-gray-600 hover:border-gray-500'}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('image-upload').click()}
                data-testid="profile-image-dropzone"
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="Profile" className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <div className="text-center p-2">
                    <Camera className="h-8 w-8 text-gray-400 mx-auto mb-1" />
                    <span className="text-xs text-gray-400">{isDragging ? 'Drop image' : 'Drop or click'}</span>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('image-upload').click()}
                    className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10 mr-3"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Photo
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCameraCapture}
                    className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Take Photo
                  </Button>
                  {/* Hidden mobile-native camera-capture file input used as a
                      fallback whenever getUserMedia is blocked by the production
                      Permissions-Policy header. */}
                  <input
                    id="participant-image-camera"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleCameraFileFallback}
                    className="hidden"
                    data-testid="participant-image-camera-fallback"
                  />
                </div>
                <p className="text-sm text-gray-400">Upload, drag & drop, or capture with camera</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className="text-gray-300">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="lastName" className="text-gray-300">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="position" className="text-gray-300">Position *</Label>
                <Select 
                  key={`position-${editingParticipant?.id || 'new'}`}
                  value={formData.position || ""} 
                  onValueChange={(value) => {
                    setFormData(prev => ({ ...prev, position: value }));
                  }}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    {availablePositions.map((position, index) => (
                      <SelectItem 
                        key={position} 
                        value={position} 
                        className={
                          editingParticipant && editingParticipant.position === position && index === 0 && !positions.includes(position)
                            ? "text-yellow-300 focus:bg-gray-600" 
                            : "text-white focus:bg-gray-600"
                        }
                      >
                        {position}
                        {editingParticipant && editingParticipant.position === position && index === 0 && !positions.includes(position) && " (Current)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="languages" className="text-gray-300">Languages</Label>
                <Input
                  id="languages"
                  value={formData.languages}
                  onChange={(e) => setFormData(prev => ({ ...prev, languages: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="e.g. English, French"
                  data-testid="participant-languages-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-300">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={handleEmailChange}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="username@domain.com"
                required
              />
              {validationErrors.email && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="homePhone" className="text-gray-300">Home Phone</Label>
                <Input
                  id="homePhone"
                  type="tel"
                  value={formData.homePhone}
                  onChange={handleHomePhoneChange}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="123-456-7890"
                />
                {validationErrors.homePhone && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.homePhone}</p>
                )}
              </div>
              <div>
                <Label htmlFor="cellPhone" className="text-gray-300">Cell Phone *</Label>
                <Input
                  id="cellPhone"
                  type="tel"
                  value={formData.cellPhone}
                  onChange={handleCellPhoneChange}
                  className={`bg-gray-700 border-gray-600 text-white ${validationErrors.cellPhone ? 'border-red-500' : ''}`}
                  placeholder="123-456-7890"
                  required
                />
                {validationErrors.cellPhone && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.cellPhone}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="skillsQuals" className="text-gray-300">Skills / Quals</Label>
              <Textarea
                id="skillsQuals"
                value={formData.skillsQuals}
                onChange={(e) => setFormData(prev => ({ ...prev, skillsQuals: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="e.g. EMR, ICS-300, HAM Radio Operator, First Aid Instructor"
                rows={2}
                data-testid="participant-skills-quals-input"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Address Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="address" className="text-gray-300">Street Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="123 Main Street"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city" className="text-gray-300">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="provinceState" className="text-gray-300">Province/State/Region</Label>
                {(formData.country === 'Canada' || formData.country === 'USA' || formData.country === 'United Kingdom') ? (
                  <Select 
                    value={formData.provinceState} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, provinceState: value }))}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder={formData.country === 'United Kingdom' ? 'Select Country/Region' : 'Select Province/State'} />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600 max-h-60">
                      {(formData.country === 'Canada' 
                        ? canadianProvinces 
                        : formData.country === 'USA' 
                          ? usStates 
                          : ukRegions
                      ).map((item) => (
                        <SelectItem 
                          key={item} 
                          value={item} 
                          className="text-white focus:bg-gray-600"
                        >
                          {item}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="provinceState"
                    value={formData.provinceState}
                    onChange={(e) => setFormData(prev => ({ ...prev, provinceState: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Enter Province/State"
                  />
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="country" className="text-gray-300">Country</Label>
              <Select 
                value={formData.country} 
                onValueChange={(value) => setFormData(prev => ({ 
                  ...prev, 
                  country: value,
                  provinceState: '' // Clear province/state when country changes
                }))}
              >
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600 max-h-60">
                  {countries.map((country) => (
                    <SelectItem 
                      key={country} 
                      value={country} 
                      className="text-white focus:bg-gray-600"
                      disabled={country === '---'}
                    >
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="latitude" className="text-gray-300">Latitude</Label>
                <Input
                  id="latitude"
                  type="text"
                  value={formData.latitude}
                  onChange={handleParticipantLatitudeChange}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="45.1234"
                />
                {validationErrors.latitude && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.latitude}</p>
                )}
              </div>
              <div>
                <Label htmlFor="longitude" className="text-gray-300">Longitude</Label>
                <Input
                  id="longitude"
                  type="text"
                  value={formData.longitude}
                  onChange={handleParticipantLongitudeChange}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="-97.0000"
                />
                {validationErrors.longitude && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.longitude}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Check-In Information Section */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Check-In Information ( Front of T-Card )</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Last Day Worked and Order Request Number */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ldw" className="text-gray-300">Last Day Worked</Label>
                <Input
                  id="ldw"
                  type="date"
                  value={formData.ldw}
                  onChange={(e) => setFormData(prev => ({ ...prev, ldw: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white text-sm"
                />
              </div>
              <div>
                <Label htmlFor="orderRequestNumber" className="text-gray-300">Order Request Number</Label>
                <Input
                  id="orderRequestNumber"
                  value={formData.orderRequestNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, orderRequestNumber: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white text-sm"
                  placeholder="Enter order request number"
                />
              </div>
            </div>

            {/* Date and Time of Check-in */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="checkInDate" className="text-gray-300">Date of Check-in</Label>
                <Input
                  id="checkInDate"
                  type="date"
                  value={formData.checkInDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, checkInDate: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="checkInTime" className="text-gray-300">Time of Check-in (24hr)</Label>
                <Input
                  id="checkInTime"
                  type="text"
                  placeholder="23:35"
                  pattern="[0-2][0-9]:[0-5][0-9]"
                  maxLength="5"
                  value={formData.checkInTime}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^0-9:]/g, '');
                    if (value.length === 2 && !value.includes(':')) {
                      value = value + ':';
                    }
                    if (value.length <= 5) {
                      setFormData(prev => ({ ...prev, checkInTime: value }));
                    }
                  }}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>

            {/* Leaders Name and Home Base */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="leadersName" className="text-gray-300">Leaders Name</Label>
                <Input
                  id="leadersName"
                  value={formData.leadersName}
                  onChange={(e) => setFormData(prev => ({ ...prev, leadersName: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Enter leaders name"
                />
              </div>
              <div>
                <Label htmlFor="homeBase" className="text-gray-300">Home Base</Label>
                <Input
                  id="homeBase"
                  value={formData.homeBase}
                  onChange={(e) => setFormData(prev => ({ ...prev, homeBase: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Enter home base"
                />
              </div>
            </div>

            {/* Departure Point and Method of Travel */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="departurePoint" className="text-gray-300">Departure Point</Label>
                <Input
                  id="departurePoint"
                  value={formData.departurePoint}
                  onChange={(e) => setFormData(prev => ({ ...prev, departurePoint: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Enter departure point"
                />
              </div>
              <div>
                <Label htmlFor="methodOfTravel" className="text-gray-300">Method of Travel</Label>
                <Select 
                  value={formData.methodOfTravel || ""} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, methodOfTravel: value }))}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Select method of travel" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    <SelectItem value="Vehicle" className="text-white focus:bg-gray-600">Vehicle</SelectItem>
                    <SelectItem value="Airlines" className="text-white focus:bg-gray-600">Airlines</SelectItem>
                    <SelectItem value="Bus" className="text-white focus:bg-gray-600">Bus</SelectItem>
                    <SelectItem value="Rail" className="text-white focus:bg-gray-600">Rail</SelectItem>
                    <SelectItem value="Helicopter" className="text-white focus:bg-gray-600">Helicopter</SelectItem>
                    <SelectItem value="Taxi /UBER" className="text-white focus:bg-gray-600">Taxi /UBER</SelectItem>
                    <SelectItem value="Other" className="text-white focus:bg-gray-600">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Current/Assigned Location & Agency/Organization */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="currentAssignedLocation" className="text-gray-300">
                    Current/Assigned Location
                  </Label>
                  <span className="text-xs text-gray-400">
                    Add markers in <span className="text-orange-400">Mapping</span>
                  </span>
                </div>
                <Select 
                  value={formData.currentAssignedLocation || 'none'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, currentAssignedLocation: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="none" className="text-white hover:bg-gray-700">
                      None
                    </SelectItem>
                    {assignedToOptions.length > 0 ? (
                      assignedToOptions.map((location) => (
                        <SelectItem key={location} value={location} className="text-white hover:bg-gray-700">
                          {location}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-400 text-sm">
                        No locations available. Add markers in Mapping.
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="organization" className="text-gray-300">
                  Agency/Organization
                </Label>
                <Input
                  id="organization"
                  value={formData.organization}
                  onChange={(e) => setFormData(prev => ({ ...prev, organization: e.target.value }))}
                  placeholder="Enter agency or organization"
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
            </div>

            {/* Requested Arrival Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="requestedArrDate" className="text-gray-300">Requested Arrival Date</Label>
                <Input
                  id="requestedArrDate"
                  type="date"
                  value={formData.requestedArrDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, requestedArrDate: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="requestedArrTime" className="text-gray-300">Requested Arrival Time</Label>
                <Input
                  id="requestedArrTime"
                  type="text"
                  placeholder="23:35"
                  pattern="[0-2][0-9]:[0-5][0-9]"
                  maxLength="5"
                  value={formData.requestedArrTime}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^0-9:]/g, '');
                    if (value.length === 2 && !value.includes(':')) {
                      value = value + ':';
                    }
                    if (value.length <= 5) {
                      setFormData(prev => ({ ...prev, requestedArrTime: value }));
                    }
                  }}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>

            {/* Task Force & Strike Team */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="taskForce" className="text-gray-300">Task Force</Label>
                <Select
                  value={formData.taskForce}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, taskForce: value }))}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="task-force-select">
                    <SelectValue placeholder="Select Task Force" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    <SelectItem value="none" className="text-gray-400">No Task Force assigned</SelectItem>
                    {taskForces.map(tf => (
                      <SelectItem key={tf.id} value={tf.name} className="text-white">{tf.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="strikeTeam" className="text-gray-300">Strike Team</Label>
                <Select
                  value={formData.strikeTeam}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, strikeTeam: value }))}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="strike-team-select">
                    <SelectValue placeholder="Select Strike Team" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    <SelectItem value="none" className="text-gray-400">No Strike Team assigned</SelectItem>
                    {strikeTeams.map(st => (
                      <SelectItem key={st.id} value={st.name} className="text-white">{st.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Qualifications */}
            <div>
              <Label htmlFor="qualifications" className="text-gray-300">Qualifications</Label>
              <textarea
                id="qualifications"
                value={formData.qualifications}
                onChange={(e) => setFormData(prev => ({ ...prev, qualifications: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white w-full min-h-[80px] p-3 rounded-md resize-vertical"
                placeholder="Enter qualifications"
              />
            </div>

            {/* Back of T-Card Section */}
            <div className="border-t border-gray-600 pt-4 mt-4">
              <h3 className="text-orange-500 text-lg font-semibold mb-4">Check-In Information ( Back of T-Card )</h3>
              
              {/* Incident Location and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="tcardIncidentLocation" className="text-gray-300">Incident Location:</Label>
                  <Input
                    id="tcardIncidentLocation"
                    value={formData.tcardIncidentLocation}
                    onChange={(e) => setFormData(prev => ({ ...prev, tcardIncidentLocation: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white text-sm"
                    placeholder="Enter incident location"
                    data-testid="tcard-incident-location"
                  />
                </div>
                <div>
                  <Label htmlFor="tcardTime" className="text-gray-300">Time:</Label>
                  <Input
                    id="tcardTime"
                    type="text"
                    placeholder="23:35"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.tcardTime}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        setFormData(prev => ({ ...prev, tcardTime: value }));
                      }
                    }}
                    className="bg-gray-700 border-gray-600 text-white text-sm"
                    data-testid="tcard-time"
                  />
                </div>
              </div>

              {/* Status Checkboxes */}
              <div className="mb-4">
                <Label className="text-gray-300 mb-2 block">Status</Label>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="tcardStatusAssigned"
                      checked={formData.tcardStatusAssigned}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, tcardStatusAssigned: checked }))}
                      data-testid="tcard-status-assigned"
                    />
                    <Label htmlFor="tcardStatusAssigned" className="text-gray-300 text-sm">Assigned</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="tcardStatusOSRest"
                      checked={formData.tcardStatusOSRest}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, tcardStatusOSRest: checked }))}
                      data-testid="tcard-status-os-rest"
                    />
                    <Label htmlFor="tcardStatusOSRest" className="text-gray-300 text-sm">O/S Rest</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="tcardStatusOSPers"
                      checked={formData.tcardStatusOSPers}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, tcardStatusOSPers: checked }))}
                      data-testid="tcard-status-os-pers"
                    />
                    <Label htmlFor="tcardStatusOSPers" className="text-gray-300 text-sm">O/S Pers</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="tcardStatusAvailable"
                      checked={formData.tcardStatusAvailable}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, tcardStatusAvailable: checked }))}
                      data-testid="tcard-status-available"
                    />
                    <Label htmlFor="tcardStatusAvailable" className="text-gray-300 text-sm">Available</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="tcardStatusOSMech"
                      checked={formData.tcardStatusOSMech}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, tcardStatusOSMech: checked }))}
                      data-testid="tcard-status-os-mech"
                    />
                    <Label htmlFor="tcardStatusOSMech" className="text-gray-300 text-sm">O/S Mech</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="tcardStatusETR"
                      checked={formData.tcardStatusETR}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, tcardStatusETR: checked }))}
                      data-testid="tcard-status-etr"
                    />
                    <Label htmlFor="tcardStatusETR" className="text-gray-300 text-sm">ETR</Label>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="tcardNotes" className="text-gray-300">Notes:</Label>
                <textarea
                  id="tcardNotes"
                  value={formData.tcardNotes}
                  onChange={(e) => setFormData(prev => ({ ...prev, tcardNotes: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white w-full min-h-[80px] p-3 rounded-md resize-vertical"
                  placeholder="Enter notes"
                  data-testid="tcard-notes"
                />
              </div>
            </div>

            {/* Sent to Resource Unit */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sentToResourceUnit"
                checked={formData.sentToResourceUnit}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, sentToResourceUnit: checked }))}
              />
              <Label htmlFor="sentToResourceUnit" className="text-gray-300">
                Sent to Resource Unit
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Save Step Button */}
        <div className="flex justify-end">
          <Button 
            onClick={saveStepDraft}
            disabled={loading}
            variant="outline"
            className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : 'Save Step'}
          </Button>
        </div>

        <div className="flex justify-end space-x-4 pt-6">
          <Button type="button" variant="outline" onClick={onBack} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingParticipant ? 'Update Participant' : 'Add Participant')}
          </Button>
        </div>
      </form>

      {/* Location Management Dialog */}
      {showAddLocationDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg max-w-lg w-full mx-4 border border-gray-700">
            <h3 className="text-xl font-bold text-orange-500 mb-4">Manage Check-in Locations</h3>
            
            {/* Add New Location Section */}
            <div className="space-y-4 mb-6">
              <div>
                <Label className="text-gray-300">Add New Location</Label>
                <div className="space-y-2 mt-2">
                  <Input
                    value={newLocationName}
                    onChange={(e) => setNewLocationName(e.target.value)}
                    placeholder="Location name *"
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                  <Input
                    value={newLocationDescription}
                    onChange={(e) => setNewLocationDescription(e.target.value)}
                    placeholder="Description (optional)"
                    className="bg-gray-700 border-gray-600 text-white"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddLocation();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={handleAddLocation}
                    className="bg-blue-600 hover:bg-blue-700 w-full"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Location
                  </Button>
                </div>
              </div>
            </div>

            {/* Existing Locations List */}
            <div className="mb-6">
              <Label className="text-gray-300 mb-2 block">Select or Manage Locations</Label>
              {formData.assignedTo && (
                <div className="mb-2 p-2 bg-blue-600 rounded text-white text-sm flex items-center justify-between">
                  <span>Current Selection: <strong>{formData.assignedTo}</strong></span>
                  <CheckCircle className="h-4 w-4" />
                </div>
              )}
              <div className="bg-gray-900 rounded-lg p-3 max-h-60 overflow-y-auto">
                {/* Default Locations (Cannot Delete, Can Select) */}
                {defaultLocations.map((location) => (
                  <div 
                    key={location} 
                    className={`flex items-center justify-between py-2 px-3 rounded cursor-pointer transition-colors ${
                      formData.assignedTo === location 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'hover:bg-gray-700'
                    }`}
                    onClick={() => {
                      console.log('🔵 Selecting location:', location);
                      console.log('🔵 Current formData.assignedTo:', formData.assignedTo);
                      setFormData(prev => {
                        console.log('🔵 Previous state:', prev.assignedTo);
                        const newState = { ...prev, assignedTo: location };
                        console.log('🔵 New state:', newState.assignedTo);
                        return newState;
                      });
                      toast.success(`✓ Selected: ${location}`);
                    }}
                  >
                    <span className="text-white">{location}</span>
                    <div className="flex items-center gap-2">
                      {formData.assignedTo === location && (
                        <CheckCircle className="h-4 w-4 text-white" />
                      )}
                      <span className="text-xs text-gray-500">Default</span>
                    </div>
                  </div>
                ))}
                
                {/* Custom Locations (Can Delete and Select) */}
                {locations.map((location) => (
                  <div 
                    key={location.id} 
                    className={`flex items-center justify-between py-2 px-3 rounded group transition-colors ${
                      formData.assignedTo === location.name 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'hover:bg-gray-700'
                    }`}
                  >
                    <span 
                      className="text-white flex-1 cursor-pointer"
                      onClick={() => {
                        console.log('🔵 Selecting custom location:', location.name);
                        console.log('🔵 Current formData.assignedTo:', formData.assignedTo);
                        setFormData(prev => {
                          console.log('🔵 Previous state:', prev.assignedTo);
                          const newState = { ...prev, assignedTo: location.name };
                          console.log('🔵 New state:', newState.assignedTo);
                          return newState;
                        });
                        toast.success(`✓ Selected: ${location.name}`);
                      }}
                    >
                      {location.name}
                    </span>
                    <div className="flex items-center gap-2">
                      {formData.assignedTo === location.name && (
                        <CheckCircle className="h-4 w-4 text-white" />
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLocation(location.id, location.name);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-600 rounded transition-all"
                        title="Delete location"
                      >
                        <Trash className="h-4 w-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {locations.length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-4">No custom locations yet</p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={() => {
                  setShowAddLocationDialog(false);
                  setNewLocationName('');
                  setNewLocationDescription('');
                }}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default ParticipantForm;
